import { App } from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import { getOrCreateDeviceToken } from "@/core/services/DeviceToken";
import { AxiosResponse, AxiosRequestConfig, AxiosError } from "axios";
import { tricklingProgress } from '@/core/plugins/trickling-progress';
import { notifError } from "../helpers/notification";
import { endSession, followLogoutFromOtherTab, isUserRecentlyActive } from "./IdleSession";
import { isCachedMasterDataWrite, useApiStore } from "@/store/apiStore";


/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;

  /**
   * @description setup abort controller
   */
  public static abortController = new AbortController();

  /**
   * @description reset abort controller
   */
  public static resetAbortController() {
    this.abortController = new AbortController();
  }

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.withCredentials = true;
    ApiService.vueInstance.axios.defaults.xsrfCookieName = "XSRF-TOKEN";
    ApiService.vueInstance.axios.defaults.xsrfHeaderName = "X-XSRF-TOKEN";
    ApiService.vueInstance.axios.interceptors.request.use(
      (config) => {
        tricklingProgress.start();
        config.signal = this.abortController.signal;
        config.headers.set("X-User-Active", isUserRecentlyActive() ? "1" : "0");
        // P5-01: API token lives in the Laravel session cookie; do not attach Bearer.
        if (config.headers?.has?.("Authorization")) {
          config.headers.delete("Authorization");
        }
        const csrf = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
        if (csrf) {
          config.headers.set("X-CSRF-TOKEN", csrf);
        }
        try {
          config.headers.set("X-Device-Token", getOrCreateDeviceToken());
        } catch {
          /* ignore storage failures */
        }
        return config;
      }
    );
    ApiService.vueInstance.axios.interceptors.response.use(
      (response) => {
        tricklingProgress.done();
        if (isCachedMasterDataWrite(response.config)) {
          useApiStore().clearCache();
        }
        return response;
      },
      (error: AxiosError) => {
        tricklingProgress.done();
        const status = error.response?.status;
        const errorData = error.response?.data as any || null;
        if (ApiService.isAuthSessionFailure(status, errorData)) {
          if (!JwtService.hasAuth()) {
            followLogoutFromOtherTab();
            return Promise.reject(error.response);
          }
          endSession(ApiService.isSignedInElsewhere(errorData) ? "device" : "session");
          return Promise.reject(error.response);
        } else if ([500].includes(status!)) {
          notifError(error.response);
          return Promise.reject(error.response);
        } else {
          return Promise.reject(error.response);
        }
      }
    );
  }

  /**
   * True only when the server rejected the session itself.
   * Several endpoints still return 401 for form errors; those keep the session.
   */
  public static isAuthSessionFailure(status: number | undefined, errorData: any = null): boolean {
    if (status === 419) return true;
    if (status !== 401) return false;
    if (errorData?.status === 'form-error') return false;
    const message = String(errorData?.message || '').toLowerCase();
    if (message.includes('password verification failed')) return false;
    return (
      message.includes('token expired')
      || message.includes('token not provided')
      || message.includes('unauthenticated')
      || message.includes('user categories not authentication')
      || message.includes('session expired')
      // backend CheckTokenExpiry: 12h hard cap, and a newer login past the 5-device limit
      || message.includes('token absolute lifetime exceeded')
      || message.includes('signed in on another device')
    );
  }

  /** The account signed in on more devices than allowed and this one was the least recently used. */
  public static isSignedInElsewhere(errorData: any = null): boolean {
    return String(errorData?.message || '').toLowerCase().includes('signed in on another device');
  }

  /**
   * @description set the default HTTP request headers
   */
  public static setHeader(): void {
    delete ApiService.vueInstance.axios.defaults.headers.common["Authorization"];
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
    const csrf = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    if (csrf) {
      ApiService.vueInstance.axios.defaults.headers.common["X-CSRF-TOKEN"] = csrf;
    }
  }

    /**
   * @description set free HTTP request headers
   */
  public static setFreeHeader(): void {
    delete ApiService.vueInstance.axios.defaults.headers.common["Authorization"];
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
    ApiService.vueInstance.axios.defaults.headers.common["Content-Type"] =
      "application/json";
    const csrf = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    if (csrf) {
      ApiService.vueInstance.axios.defaults.headers.common["X-CSRF-TOKEN"] = csrf;
    }
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description send the GET HTTP request
   * @param resource: string
   * @param params: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static get(
    resource: string,
    // slug = "" as string,
    params?: AxiosRequestConfig | undefined
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description set the POST HTTP request
   * @param resource: string
   * @param params: data
   * @param config: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    params?: any | null,
    config?: AxiosRequestConfig | undefined,
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, params, config);
  }

  /**
   * @description send the UPDATE HTTP request
   * @param resource: string
   * @param slug: string
   * @param params: data
   * @param config: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params?: any | null,
    config?: AxiosRequestConfig | undefined,
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params, config);
  }

  /**
   * @description Send the PUT HTTP request
   * @param resource: string
   * @param params: data
   * @param config: AxiosRequestConfig
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params?: any | null,
    config?: AxiosRequestConfig | undefined,
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params, config);
  }

  /**
   * @description Send the DELETE HTTP request
   * @param resource: string
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource);
  }
}

export default ApiService;
